import { FiBook } from 'react-icons/fi';

export const awlSublist45Data = {
  courseId: 'awl-sublist-4-5',
  courseType: 'vocabulary',
  title: 'Academic Word List: Sublists 4-5',
  icon: FiBook,
  overview: {
    impact: 'Strengthen your academic vocabulary with intermediate-frequency AWL words',
    focus: '12 key words from Coxhead\'s AWL Sublists 4 and 5',
    uses: 'Reading, Writing Task 2',
    duration: '20-25 minutes',
  },
  sections: [
    {
      title: 'Word Meanings and Usage',
      content: '<p>Sublists 4-5 contain words that appear less frequently than Sublists 1-3 but are still common in academic texts. Mastering these gives you a clear advantage in IELTS Reading comprehension and Writing sophistication.</p>',
      points: [
        'These words are common in academic texts on policy, economics, and social science',
        'Many IELTS Reading passages use these words in paraphrased form',
        'They help you express precise ideas in Writing Task 2 arguments',
      ],
      table: {
        headers: ['Word', 'Definition', 'Example Sentence'],
        rows: [
          ['access', 'The right or opportunity to use or approach something', 'All students should have equal access to quality education.'],
          ['adequate', 'Sufficient or satisfactory for a particular purpose', 'The accommodation was adequate but not luxurious.'],
          ['annual', 'Occurring once every year', 'The annual report showed a steady increase in revenue.'],
          ['apparent', 'Clearly visible or understood; seeming but not necessarily true', 'It became apparent that the policy had failed to achieve its goals.'],
          ['approximate', 'Close to an exact amount but not completely accurate', 'The approximate cost of the project is two million dollars.'],
          ['attitude', 'A settled way of thinking or feeling about something', 'Public attitudes towards climate change have shifted significantly.'],
          ['attribute', 'A quality or feature; to regard as caused by', 'Patience is a key attribute of effective teachers.'],
          ['capacity', 'The maximum amount that something can contain or produce', 'The stadium has a capacity of 50,000 spectators.'],
          ['challenge', 'A demanding task or situation that tests abilities', 'One of the main challenges facing cities is affordable housing.'],
          ['clause', 'A section of a legal document or a grammatical unit', 'The contract includes a clause about early termination.'],
          ['decline', 'To decrease in quantity, quality, or importance', 'There has been a steady decline in newspaper readership.'],
          ['document', 'A written record; to record in detail', 'The researchers documented their findings in a peer-reviewed journal.'],
        ],
      },
    },
    {
      title: 'Word Families',
      content: '<p>Sublists 4-5 words have versatile forms that appear across IELTS task types. Knowing all forms helps you paraphrase in Reading and vary your grammar in Writing.</p>',
      points: [
        'Many of these words function as both nouns and verbs (e.g. "challenge", "document", "decline")',
        'Adjective and adverb forms are essential for descriptive academic writing',
      ],
      table: {
        headers: ['Base Word', 'Noun', 'Verb', 'Adjective/Adverb'],
        rows: [
          ['access', 'access, accessibility', 'access', 'accessible'],
          ['adequate', 'adequacy', '—', 'adequate, adequately, inadequate'],
          ['annual', '—', '—', 'annual, annually'],
          ['apparent', '—', '—', 'apparent, apparently'],
          ['approximate', 'approximation', 'approximate', 'approximate, approximately'],
          ['attitude', 'attitude', '—', 'attitudinal'],
          ['attribute', 'attribute, attribution', 'attribute', 'attributable'],
          ['capacity', 'capacity', '—', '—'],
          ['challenge', 'challenge, challenger', 'challenge', 'challenging'],
          ['clause', 'clause', '—', 'clausal'],
          ['decline', 'decline', 'decline', 'declining'],
          ['document', 'document, documentation', 'document', 'documentary, documented'],
        ],
      },
    },
    {
      title: 'Common Collocations',
      content: '<p>Learning natural word partnerships helps you write fluently and recognise paraphrases in Reading passages.</p>',
      points: [
        'IELTS Reading often paraphrases using collocations — recognising them speeds comprehension',
        'Using precise collocations in Writing shows vocabulary depth to examiners',
      ],
      examples: [
        { label: 'access', text: 'gain access to, have access to, equal access, limited access, access to information' },
        { label: 'adequate', text: 'adequate provision, adequate funding, adequate resources, perfectly adequate' },
        { label: 'annual', text: 'annual report, annual income, annual growth, on an annual basis' },
        { label: 'apparent', text: 'immediately apparent, become apparent, for no apparent reason, apparently simple' },
        { label: 'attitude', text: 'positive attitude, change in attitude, public attitude towards, adopt an attitude' },
        { label: 'attribute', text: 'key attribute, attribute to (verb), personal attributes, attributed to' },
        { label: 'capacity', text: 'full capacity, limited capacity, capacity to (do), in the capacity of' },
        { label: 'challenge', text: 'face a challenge, major challenge, meet the challenge, challenging task' },
        { label: 'decline', text: 'steady decline, sharp decline, decline in, decline to (do)' },
        { label: 'document', text: 'official document, legal document, well-documented, document the process' },
      ],
    },
    {
      title: 'Using AWL Words in IELTS Writing',
      content: '<p>Sublists 4-5 words are especially useful for describing trends, evaluating proposals, and discussing access and opportunity — common Task 2 themes.</p>',
      points: [
        'Use "decline" for describing downward trends instead of informal "go down"',
        'Replace "enough" with "adequate" and "about" with "approximately" for formal register',
        'Use "challenge" as both a noun and verb to add variety to your writing',
      ],
      examples: [
        { label: 'Weak sentence', text: 'The number of people reading newspapers has gone down a lot in the last ten years.' },
        { label: 'Strong sentence', text: 'There has been a significant decline in newspaper readership over the past decade.' },
        { label: 'Weak sentence', text: 'People cannot easily get good healthcare in rural areas.' },
        { label: 'Strong sentence', text: 'Access to adequate healthcare remains a major challenge in rural communities.' },
      ],
    },
  ],
  exercises: [
    {
      title: 'Match Word to Definition',
      type: 'select',
      instructions: 'Choose the correct definition for each academic word.',
      questions: [
        { text: 'What does "adequate" mean?', options: ['More than enough', 'Sufficient for a particular purpose', 'Completely lacking'], answer: 'Sufficient for a particular purpose', explanation: '"Adequate" means just enough — satisfactory but not exceptional.' },
        { text: 'What does "apparent" mean?', options: ['Hidden from view', 'Clearly visible or understood', 'Completely false'], answer: 'Clearly visible or understood', explanation: '"Apparent" can mean both "obvious" and "seeming" (but not necessarily true).' },
        { text: 'What does "capacity" mean?', options: ['The maximum amount something can hold or produce', 'A type of container', 'The speed of an operation'], answer: 'The maximum amount something can hold or produce', explanation: '"Capacity" refers to the total amount possible, whether physical space or ability.' },
        { text: 'What does "attribute" (noun) mean?', options: ['A quality or characteristic', 'A type of graph', 'A verbal instruction'], answer: 'A quality or characteristic', explanation: 'As a noun, "attribute" means a feature or quality. As a verb, it means to credit a cause.' },
        { text: 'What does "decline" mean?', options: ['To increase rapidly', 'To decrease in quantity or quality', 'To accept an invitation'], answer: 'To decrease in quantity or quality', explanation: '"Decline" means a reduction or downward trend. It can also mean to politely refuse.' },
      ],
    },
    {
      title: 'Complete the Sentence',
      type: 'fill-in',
      instructions: 'Fill in the blank with the correct word from Sublists 4-5.',
      questions: [
        { text: 'The ___ cost of the renovation is around $500,000.', answer: 'approximate', explanation: '"Approximate" means close to but not exact — appropriate for estimates.' },
        { text: 'Rural communities often lack ___ to reliable internet services.', answer: 'access', explanation: '"Access to" is a key collocation meaning the ability to use or reach something.' },
        { text: 'Public ___ towards recycling have become more positive in recent years.', answer: 'attitudes', explanation: '"Attitudes towards" is the standard collocation for expressing how people think about a topic.' },
        { text: 'The ___ growth rate of the economy was approximately 3.5%.', answer: 'annual', explanation: '"Annual growth rate" is a common collocation in economics and data description.' },
      ],
    },
    {
      title: 'Choose the Correct Word Form',
      type: 'select',
      instructions: 'Select the grammatically correct form of the word for each sentence.',
      questions: [
        { text: 'The museum is ___ to wheelchair users via a ramp. (access)', options: ['accessible', 'access', 'accessibility'], answer: 'accessible', explanation: 'An adjective is needed after "is" — "accessible" means able to be reached or used.' },
        { text: 'The ___ of the evidence is well established. (adequate)', options: ['adequacy', 'adequate', 'adequately'], answer: 'adequacy', explanation: '"The adequacy of" requires the noun form.' },
        { text: 'The ___ number of visitors last year was around 2 million. (approximate)', options: ['approximately', 'approximate', 'approximation'], answer: 'approximate', explanation: 'An adjective is needed before the noun "number" — "approximate" modifies it.' },
        { text: 'The success was largely ___ to strong leadership. (attribute)', options: ['attributable', 'attribute', 'attribution'], answer: 'attributable', explanation: '"Attributable to" is the adjective form meaning able to be attributed to a cause.' },
      ],
    },
    {
      title: 'Use Words in Sentences',
      type: 'writing',
      instructions: 'Write a sentence using the given word appropriately in an academic context.',
      questions: [
        { text: 'Write a sentence using "decline" to describe a social trend.', example: 'The decline in face-to-face communication can be attributed to the rise of social media platforms.', tip: 'Use "decline in" (noun) or "has declined" (verb) — both work in trend descriptions.' },
        { text: 'Write a sentence using "challenge" about urban planning.', example: 'One of the greatest challenges facing urban planners is balancing economic development with environmental sustainability.', tip: 'Try "face a challenge", "major challenge", or "challenging task" for natural collocations.' },
        { text: 'Write a sentence using "access" about education inequality.', example: 'Children in developing countries often have limited access to quality education and learning resources.', tip: 'Use "access to" — it collocates with education, healthcare, technology, and information.' },
      ],
    },
  ],
  tips: [
    '"Decline" is invaluable for IELTS Writing Task 1 — use it to describe downward trends in graphs',
    'Learn both meanings of "attribute": a quality (noun) and to credit a cause (verb, + to)',
    '"Access" and "adequate" appear in almost every IELTS topic — practise using them across themes',
    'Replace informal expressions: "go down" → "decline", "enough" → "adequate", "about" → "approximately"',
  ],
  dailyPractice: [
    'Describe a graph trend using "decline", "annual", and "approximate" in a paragraph',
    'Write a Task 2 introduction on education using "access", "adequate", and "challenge"',
    'Read an IELTS Reading passage and circle all Sublists 4-5 words you find',
    'Create word-form tables for 4 new words from memory and check against the lesson',
  ],
  backPath: '/exams/english/#vocabulary',
  backLabel: 'Back to Vocabulary',
  aiContext: 'AWL Sublists 4-5 vocabulary course covering 12 key academic words: access, adequate, annual, apparent, approximate, attitude, attribute, capacity, challenge, clause, decline, document. Includes word meanings, word families, collocations, and IELTS Writing integration.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...awlSublist45Data,
      title: awlSublist45Data.title + ' (Foundations)',
      overview: { ...awlSublist45Data.overview, duration: '15-20 minutes' },
      sections: awlSublist45Data.sections.slice(0, Math.max(2, Math.ceil(awlSublist45Data.sections.length / 2))),
      exercises: awlSublist45Data.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...awlSublist45Data,
      title: awlSublist45Data.title + ' (Advanced)',
      overview: { ...awlSublist45Data.overview, duration: '30-35 minutes' },
      sections: awlSublist45Data.sections,
      exercises: awlSublist45Data.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(awlSublist45Data.tips || []),
        'For Band 8+: Use less common vocabulary naturally in context.',
        'Advanced tip: Focus on collocations and word families, not just individual words.',
      ],
    };
  }
  return awlSublist45Data;
}
